import React, {useEffect, useRef, useState} from 'react';
import {MarkerClusterer, SuperClusterAlgorithm} from "@googlemaps/markerclusterer";
import {mapStyles} from "../../../../utils/map-styles.js";
import axios from "axios";
import {WazeSubType, WazeType} from "../../../../utils/waze-types.js";
import projectUtils from "../../../../utils/project-utils.js";
import {getAssetPath} from "../../../../utils/get-asset-path.js";
import {FaLocationDot} from "react-icons/fa6";
import {FaChevronDown, FaChevronUp, FaMap, FaWaze} from "react-icons/fa";
import {GoogleMapsOverlay} from "@deck.gl/google-maps";
import {HeatmapLayer} from "@deck.gl/aggregation-layers";

const API_URL = import.meta.env.VITE_API_URL;

let heatmap, markerCluster, recoveryVehicle = [];
const resultDisplayState = {
    marker_cluster: false,
    thermal: false,
    recovery: false
}

const mapControlState = {
    neighborhoodStreets: false,
    pointsOfInterest: false,
    trafficStatus: false,
    waze: false
}

const wazeMarkers = new Map();
let wazeInterval, wazeOldIds = [];

const ResultsDisplay = ({markers, layers, map, hiddeResultDisplay, setModal, markerTarget, setMarkerTarget, hasWaze, mapLib}) => {
    const trafficLayer = new mapLib.TrafficLayer();
    const [resultDisplay, setResultDisplay] = useState(resultDisplayState);
    const [thermalRadius, setThermalRadius] = useState(10);

    const [mapControls, setMapControls] = useState(mapControlState);

    const trafficLayerRef = useRef(null);

    useEffect(() => {
        if (!trafficLayerRef.current && mapLib && map) {
            trafficLayerRef.current = new mapLib.TrafficLayer();
        }
    }, [mapLib, map]);

    useEffect(() => {
        if (!hiddeResultDisplay) return;

        clearHeatMap();
        markerCluster?.clearMarkers();

        recoveryVehicle.forEach((recovery) => recovery.setMap(null));
        recoveryVehicle = [];

        setThermalRadius(10);
        setResultDisplay(resultDisplayState);

        setMapControls(mapControlState);
        trafficLayer.setMap(null);

        map?.setOptions({styles: mapStyles(mapControlState, "RESET")});

        clearWazeMarker();
    }, [hiddeResultDisplay]);

    const handleDisplay = (target) => setResultDisplay(prev => ({...prev, [target]: !prev[target]}));

    const buildHeatmapData = () => [...markers]
        .map(([, marker]) => {
            const position = marker.getPosition();

            if (!position) return null;

            return {
                position: [position.lng(), position.lat()],
                weight: 1
            };
        })
        .filter(Boolean);

    const createHeatmapLayer = (radius) => new HeatmapLayer({
        id: "analysis-tool-heatmap",
        data: buildHeatmapData(),
        getPosition: data => data.position,
        getWeight: data => data.weight,
        radiusPixels: radius,
        intensity: 1,
        threshold: 0.05
    });

    const clearHeatMap = () => {
        heatmap?.setProps({layers: []});
        heatmap?.setMap(null);
        heatmap = null;
    }

    const toggleHeatMap = () => {
        [...markers].forEach(([, marker]) => marker.setVisible(resultDisplay.thermal));

        if (!resultDisplay.thermal) {
            heatmap = new GoogleMapsOverlay({
                layers: [createHeatmapLayer(thermalRadius)]
            });
            heatmap.setMap(map);

            return;
        }

        markerTarget?.setAnimation(google.maps.Animation.BOUNCE);
        clearHeatMap();
    }

    const toggleCluster = () => {
        if (!resultDisplay.marker_cluster) {
            markerCluster = new MarkerClusterer({
                map,
                markers: [...markers].map(([, marker]) => marker),
                algorithm: new SuperClusterAlgorithm({})
            });
            return;
        }

        markerCluster?.clearMarkers();
        [...markers].map(([, marker]) => marker.setMap(map));
        markerTarget?.setAnimation(google.maps.Animation.BOUNCE);
    }

    const handleThermalRadius = (operation) => {
        const radius = operation === "plus" ? thermalRadius + 10 : thermalRadius - 10;

        heatmap?.setProps({layers: [createHeatmapLayer(radius)]});
        setThermalRadius(radius);
    }

    const toggleRecovery = () => {
        recoveryVehicle.forEach((recovery) => recovery.setMap(null));
        recoveryVehicle = [];

        if (!resultDisplay.recovery) {
            const occMarkers = [...markers].filter(([, marker]) => marker.type === "sentry-occurrence");
            occMarkers.forEach(([, marker]) => {
                const occurrence = marker.description;

                if (occurrence.first_reason_name === 'Veículo Recuperado') {
                    const relatedOccurrences = JSON.parse(occurrence.relatedoccurrences);
                    const vehiclesOccurrence = occurrence.vehicles;

                    let recovery = false;
                    vehiclesOccurrence.forEach(function (vehicle) {
                        if (vehicle.classification === "RECUPERADO") {
                            recovery = true;
                        }
                    });

                    if (recovery) {
                        if (relatedOccurrences.length > 0 && vehiclesOccurrence.length > 0) {
                            relatedOccurrences.forEach(function (occId) {
                                [...markers].forEach(([, marker_]) => {
                                    const occ = marker_.description;
                                    if ((occ.status.status === 'CONCLUDED' && occurrence.status.status === 'CONCLUDED') || (occ.status.status === 'ACTIVE' && occurrence.status.status === 'ACTIVE')) {
                                        if (occ.id === occId.occurrenceId) {
                                            const latRobberyOrTheft = occ.system_latitude;
                                            const lngRobberyOrTheft = occ.system_longitude;

                                            const latRecovered = marker.getPosition().lat();
                                            const lngRecovered = marker.getPosition().lng();

                                            if (latRobberyOrTheft !== null && latRecovered !== null) {
                                                const flightPath = new google.maps.Polyline({
                                                    map: map,
                                                    path: [new google.maps.LatLng(latRobberyOrTheft, lngRobberyOrTheft), new google.maps.LatLng(latRecovered, lngRecovered)],
                                                    geodesic: true,
                                                    strokeColor: '#FF0000',
                                                    strokeOpacity: 1.0,
                                                    strokeWeight: 2,
                                                    icons: [{
                                                        icon: {
                                                            path: google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
                                                            scale: 3,
                                                            strokeColor: '#393',
                                                        },
                                                        offset: '100%',
                                                    }],
                                                });

                                                recoveryVehicle.push(flightPath);

                                                let count = 0;
                                                window.setInterval(function () {
                                                    count = (count + 1) % 200;

                                                    const icons = flightPath.get('icons');
                                                    icons[0].offset = (count / 2) + '%';
                                                    flightPath.set('icons', icons);
                                                }, 20);
                                            }
                                        }
                                    }
                                });
                            });
                        }
                    }
                }
            });
        }
    }

    const openItemsByLayer = () => {
        if (!layers.size) {
            projectUtils.swalMessage("warning", "", "Selecione pelo menos um setor.", "Ok");
            return;
        }

        setModal({target: "layer", show: true});
    }

    const toggleTrafficLayer = () => {
        if (!trafficLayerRef.current) return;

        const { trafficStatus } = mapControls;

        if (trafficStatus) {
            trafficLayerRef.current.setMap(null);
        } else {
            trafficLayerRef.current.setMap(map);
        }

        setMapControls(prev => ({
            ...prev,
            trafficStatus: !prev.trafficStatus
        }));
    }

    const pointsOfInterest = () => {
        const {pointsOfInterest} = mapControls;

        const style = mapStyles(mapControls, "POI");

        map.setOptions({styles: style});
        setMapControls({...mapControls, pointsOfInterest: !pointsOfInterest});
    }

    const neighborhoodStreets = () => {
        const {neighborhoodStreets} = mapControls;

        const style = mapStyles(mapControls, "NEIGHBORHOOD")

        map.setOptions({styles: style});
        setMapControls({...mapControls, neighborhoodStreets: !neighborhoodStreets});
    }

    const toggleWaze = () => {
        let {waze} = mapControls;

        if (!waze) {
            createWazeMarkers();

            projectUtils.swalMessage("warning", "Atenção", "Os dados do Waze são em tempo real", "Ok");

            wazeInterval = setInterval(async () => {
                await createWazeMarkers()
            }, 2 * 60 * 1000);
        } else clearWazeMarker();

        setMapControls({...mapControls, waze: !waze});
    }

    const createWazeMarkers = () => {
        axios.get(`${API_URL}/gis/get/waze-json`)
            .then(({data: {data}}) => {
                const {alerts} = data;

                if (!alerts.length) return;

                let addItens = alerts.filter(item => !wazeOldIds.includes(item.uuid));
                let removeItens = wazeOldIds.filter(id => !alerts.some(item => item.uuid === id));

                addItens.forEach(alert => {
                    const {location, uuid, type, subtype, city, street} = alert;

                    const marker = new google.maps.Marker({
                        id: uuid,
                        position: new google.maps.LatLng(location.y, location.x),
                        title: WazeType[type] + (subtype ? " - " + WazeSubType[subtype] : ""),
                        icon: `${API_URL}/public/assets/img/markers-gis/waze/${type}-zoom1.png`,
                        description: {
                            street,
                            city
                        },
                        type: "waze",
                        zIndex: 9,
                        map
                    });

                    marker.addListener('click', () => {
                        setMarkerTarget(prev => {
                            prev?.setAnimation(null);
                            return prev === marker ? undefined : marker
                        });

                        marker.setAnimation(google.maps.Animation.BOUNCE);
                    });

                    wazeMarkers.set(uuid, marker);
                });

                removeItens.forEach(id => {
                    if (markerTarget.id === id) setMarkerTarget(prev => {
                        prev?.setAnimation(null);
                        return undefined
                    });

                    wazeMarkers.get(id).setMap(null);
                    wazeMarkers.delete(id);
                });

                wazeOldIds = alerts.map(item => item.uuid);
            })
            .catch(error => {
                console.error(error);

                clearWazeMarker();
                setMapControls({...mapControls, waze: false});

                projectUtils.swalErrorMessage('', 'Não foi possível realizar a pesquisa.', 'Ok');
            });
    }

    const clearWazeMarker = () => {
        clearInterval(wazeInterval);

        for (const [, marker] of wazeMarkers.entries()) {
            if (markerTarget?.id === marker?.id) setMarkerTarget(prev => {
                prev?.setAnimation(null);
                return undefined
            });

            marker.setMap(null);
            wazeMarkers.delete(marker.id);
        }

        wazeOldIds = [];
    }

    const hasSentryOccurrence = [...markers].some(([, marker]) => marker.type === "sentry-occurrence");

    if (hiddeResultDisplay) return;

    return (
        <div className="bg-white border border-[#dfe5ec] rounded-xl p-3">
            <div className="text-[11px] font-semibold tracking-[0.1em] uppercase text-[#6b7a92] mb-2.5">Ferramentas</div>

            <div id="analysis-results-display" className="flex flex-col gap-1.5">
                <div className="flex gap-1 justify-center flex-wrap p-0 mb-1">
                    <button className={"mode-btn"} title="Itens por período"
                            onClick={() => setModal({target: "period", show: true})}>
                        <div className="mode-icon p-0!">
                            <img className={"w-full rounded-[9px]"} src={getAssetPath("img/markers/clock.svg")} alt=""/>
                        </div>
                    </button>

                    <button className={"mode-btn"} title="Itens por setor" onClick={() => openItemsByLayer()}>
                        <div className="mode-icon sector-icon">
                            <img className={"w-full rounded-[9px]"} src={getAssetPath("img/markers/map_layers.svg")} alt=""/>
                        </div>
                    </button>

                    <button className={"mode-btn"} title="Legenda"
                            onClick={() => setModal({target: "subtitles", show: true})}>
                        <div className="mode-icon subtitles-icon">
                            <img className={"w-full"} src={getAssetPath("img/custom_icons/subtitles.png")} alt=""/>
                        </div>
                    </button>
                </div>

                <div className="flex gap-1 justify-center flex-wrap p-0 mb-1">
                    <button className={["mode-btn", resultDisplay.recovery ? "selected" : "", !hasSentryOccurrence ? "opacity-50" : ""].join(" ")}
                            title="Recuperação de veículos"
                            disabled={!hasSentryOccurrence}
                            onClick={() => {
                                handleDisplay("recovery");
                                toggleRecovery()
                            }}>
                        <div className="mode-icon theft-recovered-icon">
                            <img className={"w-full h-full"} src={getAssetPath("img/markers/theft_recovered.svg")} alt=""/>
                        </div>
                    </button>

                    <button className={"mode-btn disabled:pointer-events-none disabled:opacity-50 " + (resultDisplay.marker_cluster ? "selected" : "")} title="Agrupamento"
                            onClick={() => {
                                handleDisplay("marker_cluster");
                                toggleCluster()
                            }} disabled={resultDisplay.thermal}>
                        <div className="mode-icon cluster-icon">
                            <img className={"w-full"} src={getAssetPath("img/markers/markerclusterer_26x26.svg")} alt=""/>
                        </div>
                    </button>

                    <button className={"mode-btn disabled:bg-[#9B9B9B] disabled:pointer-events-none disabled:opacity-50" + (resultDisplay.thermal ? "selected" : "")} title="Mapa térmico"
                            onClick={() => {
                                handleDisplay("thermal");
                                toggleHeatMap()
                            }} disabled={resultDisplay.marker_cluster}>
                        <div className="mode-icon heat-map-icon">
                            <img className={"w-full"} src={getAssetPath("img/markers/iconThermalMap.svg")} alt=""/>
                        </div>

                    </button>
                    {
                        resultDisplay.thermal &&
                        <>
                            <div className={"border border-[#b8b8b8] rounded-lg w-[50px] h-[50px] relative flex flex-nowrap items-center"} title="Raio">
                                <input className={"bg-white! border-0 w-full text-center rounded-tr-none rounded-br-none m-0 font-inherit text-inherit leading-inherit"} title="Raio" value={thermalRadius} disabled/>

                                <div className="border-l border-l-[#b8b8b8] w-[20px] flex flex-col -ml-px rounded-tl-none rounded-bl-none">
                                    <button className="py-1.25 cursor-pointer text-inherit text-[10px] flex w-full justify-center leading-none relative z-2"
                                            title="Aumentar o raio"
                                            disabled={thermalRadius >= 100}
                                            onClick={() => handleThermalRadius("plus")}
                                    >
                                        <FaChevronUp size={10}/>
                                    </button>
                                    <button className="py-1.25 cursor-pointer text-inherit text-[10px] flex w-full justify-center leading-none relative z-2 disabled:pointer-events-none disabled:opacity-50"
                                            title="Diminuir o raio"
                                            disabled={thermalRadius <= 10}
                                            onClick={() => handleThermalRadius("minus")}
                                    >
                                        <FaChevronDown size={10} />
                                    </button>
                                </div>
                            </div>
                        </>
                    }
                </div>

                <div className="flex gap-1 justify-center flex-wrap p-0">
                    <button className={"mode-btn " + (mapControls.trafficStatus ? "selected" : "")}
                            onClick={toggleTrafficLayer} title="Trânsito">
                        <div className="mode-icon traffic-icon"></div>
                    </button>
                    <button className={"mode-btn " + (mapControls.pointsOfInterest ? "selected" : "")}
                            onClick={pointsOfInterest} title="Pontos de interesse">
                        <div className="mode-icon flex! items-center justify-center">
                            <FaLocationDot size={36}/>
                        </div>
                    </button>
                    <button className={"mode-btn " + (mapControls.neighborhoodStreets ? "selected" : "")}
                            onClick={neighborhoodStreets} title="Ruas e bairros">
                        <div className="mode-icon flex! items-center justify-center">
                            <FaMap size={36}/>
                        </div>
                    </button>
                    {
                        hasWaze &&
                        <button className={"mode-btn " + (mapControls.waze ? "selected" : "")}
                                onClick={toggleWaze} title="Waze">
                            <div className="mode-icon flex! items-center justify-center">
                                <FaWaze size={36}/>
                            </div>
                        </button>
                    }
                </div>
            </div>
        </div>
    );
};

export default ResultsDisplay;
